import { normalizeAnalysisConfig } from './analysisConfig';

export type MediaFeature = 'vision' | 'transcription';
export const visualObservationKinds = ['visible_text', 'product_feature', 'offer', 'setting'] as const;
export type VisualObservationKind = typeof visualObservationKinds[number];
export function normalizeObservationKinds(value: unknown): VisualObservationKind[] {
    if (!Array.isArray(value) || value.length < 1 || value.length > visualObservationKinds.length
        || !value.every(kind => visualObservationKinds.includes(kind)) || new Set(value).size !== value.length)
        throw new Error('Invalid visual observation selection');
    return [...value];
}
export interface MediaLimits {
    enabled: true;
    max_records: number;
    max_assets: number;
    max_source_bytes: number;
    max_model_calls: number;
    estimated_work_units_limit: string;
}
export interface MarketMediaConfig {
    schema_version: 'market-media-config/v1';
    vision: { enabled: false } | (MediaLimits & { video_frame_offsets_ms: number[]; observation_kinds?: VisualObservationKind[] });
    transcription: { enabled: false } | (MediaLimits & { clips: { offset_ms: number; duration_ms: number }[] });
}
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const integer = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
/** Mirror Platform v1. Missing preserves legacy; null and coercion are not accepted. */
export function normalizeMediaConfig(value: unknown): MarketMediaConfig {
    const invalid = () => new Error('Invalid media configuration');
    if (!object(value) || Object.keys(value).length !== 3 || value.schema_version !== 'market-media-config/v1') throw invalid();
    const result: MarketMediaConfig = { schema_version: 'market-media-config/v1', vision: { enabled: false }, transcription: { enabled: false } };
    for (const name of ['vision', 'transcription'] as const) {
        const feature = value[name];
        if (!object(feature) || typeof feature.enabled !== 'boolean') throw invalid();
        if (!feature.enabled) { if (Object.keys(feature).length !== 1) throw invalid(); continue; }
        const selectedKinds = name === 'vision' && Object.prototype.hasOwnProperty.call(feature, 'observation_kinds');
        if (Object.keys(feature).length !== (selectedKinds ? 8 : 7) || !integer(feature.max_records, 1, 100) || !integer(feature.max_assets, 1, 100)
            || !integer(feature.max_source_bytes, 45, 2_000_000_000) || !integer(feature.max_model_calls, 1, 200)
            || Math.floor(feature.max_source_bytes / feature.max_assets) < 45) throw invalid();
        const analysis = normalizeAnalysisConfig({ schema_version: 'market-analysis-config/v1', enabled: true,
            max_records: feature.max_records, max_model_calls: feature.max_model_calls, estimated_work_units_limit: feature.estimated_work_units_limit });
        if (!analysis.enabled) throw invalid();
        const limits: MediaLimits = { enabled: true, max_records: feature.max_records, max_assets: feature.max_assets,
            max_source_bytes: feature.max_source_bytes, max_model_calls: feature.max_model_calls,
            estimated_work_units_limit: analysis.estimated_work_units_limit };
        const samples = name === 'vision' ? feature.video_frame_offsets_ms : feature.clips;
        if (!Array.isArray(samples) || samples.length < 1 || samples.length > 5 || feature.max_assets * samples.length > feature.max_model_calls) throw invalid();
        let end = -1;
        if (name === 'vision') {
            const offsets = samples.map(offset => {
                if (!integer(offset, 0, 600_000) || offset <= end) throw invalid();
                end = offset; return offset;
            });
            result.vision = { ...limits, video_frame_offsets_ms: offsets,
                ...(selectedKinds ? { observation_kinds: normalizeObservationKinds(feature.observation_kinds) } : {}) };
        } else {
            const clips = samples.map(clip => {
                if (!object(clip) || Object.keys(clip).length !== 2 || !integer(clip.offset_ms, 0, 600_000)
                    || !integer(clip.duration_ms, 1, 60_000) || clip.offset_ms < Math.max(0, end)
                    || clip.offset_ms + clip.duration_ms > 600_000) throw invalid();
                end = clip.offset_ms + clip.duration_ms; return { offset_ms: clip.offset_ms, duration_ms: clip.duration_ms };
            });
            result.transcription = { ...limits, clips };
        }
    }
    return result;
}
export function validMediaConfig(value: unknown): boolean {
    if (value === undefined) return true;
    try { normalizeMediaConfig(value); return true; } catch { return false; }
}
export function mediaConfigMatches(requested: unknown, echoed: unknown): boolean {
    if (requested === undefined) return true;
    try { return JSON.stringify(normalizeMediaConfig(requested)) === JSON.stringify(normalizeMediaConfig(echoed)); }
    catch { return false; }
}
